import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import prismLogo from '../assets/prism-logo.svg';
import { useApi } from '../hooks/useApi';
import { ChartRenderer, ChartDescriptionModal } from '../components/ChartRenderer';
import { useAnalyticsPackage } from '../context/AnalyticsPackageContext';
import { useUserContext } from '../context/UserContext';

const PROPERTY_TYPES = [
  { value: '1F', label: 'Single Family' },
  { value: 'C',  label: 'Condominium' },
  { value: 'L',  label: 'Land' },
];

const BUILDING_CATEGORIES = [
  { value:  0, label: 'All Building Categories',    clean: '' },
  { value: -1, label: 'All Luxury',                 clean: 'All Luxury' },
  { value: -2, label: '   Luxury Full Service',     clean: 'Luxury Full Service' },
  { value:  1, label: '      Luxe Hotel',            clean: 'Luxe Hotel' },
  { value:  2, label: '      Luxe Classic',          clean: 'Luxe Classic' },
  { value:  3, label: '      Luxe Boutique',         clean: 'Luxe Boutique' },
  { value:  4, label: '   Mid Luxe',              clean: 'Mid Luxe' },
  { value:  7, label: 'Traditional',                clean: 'Traditional' },
];

const CURRENT_YEAR = new Date().getFullYear();
const PRISM_LOGO_ASPECT = 1179 / 375.935;

const ACTIVE_BG   = '#958945';
const ACTIVE_TEXT = '#ffffff';
const LOCKED_BG   = '#f0f0f0';
const LOCKED_TEXT = '#999999';

interface Market {
  id: number;
  name: string;
  hasAccess: boolean;
  defaultPropertyType: string | null;
  photoUrl: string | null;
}

interface Neighborhood {
  id: number;
  name: string;
  hasAccess: boolean;
  photoUrl: string | null;
}

interface Subarea {
  id: number;
  name: string;
}

interface BuildingOpening {
  name: string;
  firstSale: number;
  avgPrice: number | null;
}

interface BuildingAppreciationRow {
  buildingId: number;
  building: string;
  buildingCategory: string;
  neighborhoodId: number;
  neighborhood: string;
  buildingAddress: string | null;
  units: number | null;
  resales: number;
  earliestSale: string;
  mostRecentSale: string;
  annualizedAppreciation: number;
}

interface ClosingNowRow {
  buildingId: number;
  building: string;
  buildingCategory: string;
  neighborhoodId: number;
  neighborhood: string;
  address: string | null;
  totalUnits: number | null;
  unitsSold: number | null;
  pctSoldOut: number | null;
  avgPrice: number | null;
  avgPsf: number | null;
}

interface ChartOption {
  id: number;
  name: string;
  title: string;
  description: string | null;
}

interface TimePeriod {
  id: number;
  name: string;
  startYear: number;
  startQuarter: number;
  endYear: number;
  endQuarter: number;
}

interface OrgInfo {
  id: number;
  name: string;
  logoUrl: string | null;
}

interface ChartData {
  categories: string[];
  years?: number[];
  series: { name: string; data: (number | null)[] }[];
}

function formatAppDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
}

function sortableBuildingName(name: string): string {
  return name.replace(/^the\s+/i, '');
}

const BUILDING_APP_SORT_LABELS: Record<'building' | 'address' | 'units' | 'resales' | 'openDate' | 'mostRecent' | 'appreciation', string> = {
  building: 'Building Name',
  address: 'Address',
  units: 'Units',
  resales: 'Resales',
  openDate: 'Open Date',
  mostRecent: 'Most Recent Sale',
  appreciation: 'Ann. Appreciation',
};

export function DashboardPage() {
  const { authFetch } = useApi();
  const navigate = useNavigate();
  const { addItem } = useAnalyticsPackage();
  const { showPdfLens } = useUserContext();
  const urlChartRef = useRef(new URLSearchParams(window.location.search).get('chart'));
  const urlNeighborhoodIdRef = useRef(Number(new URLSearchParams(window.location.search).get('neighborhoodId')) || null);
  const urlPropertyTypeRef = useRef(new URLSearchParams(window.location.search).get('propertyType'));
  const urlBuildingCategoryIdRef = useRef(new URLSearchParams(window.location.search).get('buildingCategoryId'));
  const urlSubareaIdRef = useRef(Number(new URLSearchParams(window.location.search).get('subareaId')) || null);

  const [markets, setMarkets] = useState<Market[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [neighborhoodsByMarket, setNeighborhoodsByMarket] = useState<Record<number, Neighborhood[]>>({});
  const [expandedMarkets, setExpandedMarkets] = useState<number[]>([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Neighborhood | null>(null);
  const [subareas, setSubareas] = useState<Subarea[]>([]);
  const [selectedSubarea, setSelectedSubarea] = useState<Subarea | null>(null);
  const [buildingOpenings, setBuildingOpenings] = useState<BuildingOpening[]>([]);
  const [buildingAppreciationData, setBuildingAppreciationData] = useState<BuildingAppreciationRow[] | null>(null);
  const [chartOptions, setChartOptions] = useState<ChartOption[]>([]);
  const [selectedChart, setSelectedChart] = useState<ChartOption | null>(null);
  const [showChartDescription, setShowChartDescription] = useState(false);
  const [propertyType, setPropertyType] = useState<string>(urlPropertyTypeRef.current ?? 'C');
  const [buildingCategoryId, setBuildingCategoryId] = useState<number | null>(
    urlBuildingCategoryIdRef.current ? Number(urlBuildingCategoryIdRef.current) || null : null
  );
  const [startYear, setStartYear] = useState<number | null>(null);
  const [endYear, setEndYear] = useState<number | null>(null);
  const [sliderValue, setSliderValue] = useState<[number, number]>([CURRENT_YEAR - 10, CURRENT_YEAR]);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(false);
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const buildingAppTableRef = useRef<HTMLTableElement>(null);
  const buildingAppPdfTableRef = useRef<HTMLTableElement>(null);
  const [buildingAppSortCol, setBuildingAppSortCol] = useState<'building' | 'address' | 'units' | 'resales' | 'openDate' | 'mostRecent' | 'appreciation'>('appreciation');
  const [buildingAppSortDir, setBuildingAppSortDir] = useState<'asc' | 'desc'>('desc');
  const [buildingAppPage, setBuildingAppPage] = useState(0);
  const closingNowPdfTableRef = useRef<HTMLTableElement>(null);
  const [closingNowData, setClosingNowData] = useState<ClosingNowRow[] | null>(null);
  const [closingNowPage, setClosingNowPage] = useState(0);
  const [showMortgagePercent, setShowMortgagePercent] = useState(false);
  const [mortgagePctData, setMortgagePctData] = useState<{ categories: string[]; data: (number | null)[] } | null>(null);
  const [showMortgageRate, setShowMortgageRate] = useState(false);
  const [mortgageRateData, setMortgageRateData] = useState<{ categories: string[]; data: (number | null)[] } | null>(null);
  const [showBuildingOpenings, setShowBuildingOpenings] = useState(true);
  const [timePeriods, setTimePeriods] = useState<TimePeriod[]>([]);

  useEffect(() => {
    authFetch('/api/me')
      .then(r => r.json())
      .then((data: any) => { if (data.organization) setOrgInfo(data.organization); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMarketId = Number(params.get('marketId')) || null;
    const urlChart    = params.get('chart');
    authFetch('/api/markets')
      .then(r => r.json())
      .then((data: Market[]) => {
        const accessible = data.filter(m => m.hasAccess);
        if (accessible.length === 0) return;
        if (!urlChart) {
          const target = urlMarketId ? accessible.find(m => m.id === urlMarketId) : null;
          navigate(`/market/${(target ?? accessible[0]).id}`, { replace: true });
          return;
        }
        setMarkets(data);
        const preferred = urlMarketId ? accessible.find(m => m.id === urlMarketId) : null;
        const marketToSet = preferred ?? accessible[0];
        setSelectedMarket(marketToSet);
        setExpandedMarkets([marketToSet.id]);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNeighborhoods = (market: Market) => {
    if (neighborhoodsByMarket[market.id] !== undefined) return;
    authFetch(`/api/markets/${market.id}/neighborhoods`)
      .then(r => r.json())
      .then((data: Neighborhood[]) => {
        setNeighborhoodsByMarket(prev => ({ ...prev, [market.id]: data }));
        const urlNhId = urlNeighborhoodIdRef.current;
        if (urlNhId) {
          const match = data.find(n => n.id === urlNhId);
          if (match) {
            urlNeighborhoodIdRef.current = null;
            setSelectedNeighborhood(match);
          }
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    setSubareas([]);
    setSelectedSubarea(null);
    if (!selectedNeighborhood) return;
    const marketId = Number(
      Object.keys(neighborhoodsByMarket).find(mid =>
        neighborhoodsByMarket[Number(mid)].some(n => n.id === selectedNeighborhood.id)
      )
    );
    if (!marketId) return;
    authFetch(`/api/markets/${marketId}/neighborhoods/${selectedNeighborhood.id}/subareas`)
      .then(r => r.json())
      .then((data: Subarea[]) => {
        setSubareas(data);
        if (urlSubareaIdRef.current) {
          const match = data.find(s => s.id === urlSubareaIdRef.current);
          if (match) setSelectedSubarea(match);
          urlSubareaIdRef.current = null;
        }
      });
  }, [selectedNeighborhood]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBuildingOpenings([]);
    const scopeParam = selectedNeighborhood
      ? `neighborhoodId=${selectedNeighborhood.id}`
      : selectedMarket
      ? `marketId=${selectedMarket.id}`
      : null;
    if (!scopeParam) return;
    const luxParam = buildingCategoryId === -1 ? '&luxOption=true'
                   : buildingCategoryId === -2 ? '&luxFullService=true' : '';
    const catParam = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';
    authFetch(`/api/charts/building-openings?${scopeParam}${luxParam}${catParam}`)
      .then(r => r.json())
      .then((data: BuildingOpening[]) => setBuildingOpenings(data));
  }, [selectedNeighborhood, selectedMarket, buildingCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedMarket) return;
    const marketNeighborhoods = neighborhoodsByMarket[selectedMarket.id] ?? [];
    if (!selectedNeighborhood || !marketNeighborhoods.some(n => n.id === selectedNeighborhood.id)) {
      setSelectedNeighborhood(null);
    }
    setChartData(null);
    setSelectedChart(null);
    if (urlPropertyTypeRef.current !== null) {
      setPropertyType(urlPropertyTypeRef.current);
      urlPropertyTypeRef.current = null;
    } else {
      setPropertyType(selectedMarket.defaultPropertyType ?? 'C');
    }
    loadNeighborhoods(selectedMarket);
    authFetch(`/api/charts?marketId=${selectedMarket.id}`)
      .then(r => r.json())
      .then((data: ChartOption[]) => {
        setChartOptions(data);
        const urlChart = urlChartRef.current;
        urlChartRef.current = null;
        const preferred = urlChart ? data.find(c => c.name === urlChart) : null;
        const salesPrices = data.find(c => c.name === 'salesPrices');
        setSelectedChart(preferred ?? salesPrices ?? data[0] ?? null);
      });
  }, [selectedMarket]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (urlBuildingCategoryIdRef.current !== null) {
      setBuildingCategoryId(Number(urlBuildingCategoryIdRef.current) || null);
      urlBuildingCategoryIdRef.current = null;
    }
    if (selectedChart?.name === 'salesPrices') {
      setStartYear(CURRENT_YEAR - 5);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 5, CURRENT_YEAR]);
    } else if (selectedChart?.name === 'salesPSF') {
      setStartYear(CURRENT_YEAR - 10);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 10, CURRENT_YEAR]);
    } else if (selectedChart?.name === 'millionDollarMarket') {
      setStartYear(CURRENT_YEAR - 10);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 10, CURRENT_YEAR]);
    } else if (selectedChart?.name === 'salesPriceDistribution') {
      setStartYear(CURRENT_YEAR - 10);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 10, CURRENT_YEAR]);
    } else if (selectedChart?.name === 'salesDistribution') {
      setStartYear(CURRENT_YEAR - 10);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 10, CURRENT_YEAR]);
    } else if (selectedChart?.name === 'luxVsTraditional') {
      setStartYear(CURRENT_YEAR - 5);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 5, CURRENT_YEAR]);
    } else if (selectedChart?.name === 'priceIndex') {
      setStartYear(CURRENT_YEAR - 19);
      setEndYear(CURRENT_YEAR);
      setSliderValue([CURRENT_YEAR - 19, CURRENT_YEAR]);
    } else {
      setStartYear(null);
      setEndYear(null);
    }
  }, [selectedChart]);

  useEffect(() => {
    const isNeighborhoodMode = !!selectedNeighborhood;
    const isMarketMode = !selectedNeighborhood && !!selectedMarket;
    if ((!isNeighborhoodMode && !isMarketMode) || !selectedChart) return;

    const needsYears = ['millionDollarMarket', 'salesPrices', 'salesPSF', 'salesPriceDistribution', 'salesDistribution', 'luxVsTraditional', 'priceIndex'].includes(selectedChart.name);
    if (needsYears && startYear === null) return;

    setLoading(true);
    setChartData(null);
    setBuildingAppreciationData(null);
    setClosingNowData(null);
    setClosingNowPage(0);

    const controller = new AbortController();
    let cancelled = false;

    if (selectedChart.name === 'buildingAppreciation') {
      const scopeParam = isNeighborhoodMode
        ? `neighborhoodId=${selectedNeighborhood!.id}`
        : `marketId=${selectedMarket!.id}`;
      const luxParam = buildingCategoryId === -1 ? '&luxOption=true'
                     : buildingCategoryId === -2 ? '&luxFullService=true'
                     : '';
      const catParam = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';
      const subareaParam = isNeighborhoodMode && selectedSubarea ? `&subareaId=${selectedSubarea.id}` : '';
      authFetch(`/api/charts/building-appreciation?${scopeParam}${luxParam}${catParam}${subareaParam}`, { signal: controller.signal })
        .then(r => r.json())
        .then((data: BuildingAppreciationRow[]) => { setBuildingAppreciationData(data); setBuildingAppPage(0); setLoading(false); })
        .catch((err: unknown) => {
          if ((err as { name?: string })?.name !== 'AbortError') setLoading(false);
        });
      return () => controller.abort();
    }

    if (selectedChart.name === 'closingNow') {
      const scopeParam = isNeighborhoodMode
        ? `neighborhoodId=${selectedNeighborhood!.id}`
        : `marketId=${selectedMarket!.id}`;
      const luxParam = buildingCategoryId === -1 ? '&luxOption=true'
                     : buildingCategoryId === -2 ? '&luxFullService=true'
                     : '';
      const catParam = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';
      authFetch(`/api/charts/closing-now?${scopeParam}${luxParam}${catParam}`, { signal: controller.signal })
        .then(r => r.json())
        .then((data: ClosingNowRow[]) => { setClosingNowData(data); setClosingNowPage(0); setLoading(false); })
        .catch((err: unknown) => {
          if ((err as { name?: string })?.name !== 'AbortError') setLoading(false);
        });
      return () => controller.abort();
    }

    const subareaParam = isNeighborhoodMode && selectedSubarea ? `&subareaId=${selectedSubarea.id}` : '';
    const base = isNeighborhoodMode
      ? `neighborhoodId=${selectedNeighborhood!.id}&propertyType=${propertyType}${subareaParam}`
      : `marketId=${selectedMarket!.id}&propertyType=${propertyType}`;
    const psfDefault = selectedChart.name === 'salesPSF';
    const priceDefault = selectedChart.name === 'salesPrices';
    const effectiveStartYear = psfDefault ? (startYear ?? CURRENT_YEAR - 10) : priceDefault ? (startYear ?? CURRENT_YEAR - 5) : startYear;
    const effectiveEndYear   = (psfDefault || priceDefault) ? (endYear ?? CURRENT_YEAR) : endYear;
    const yearParams = ['millionDollarMarket', 'salesPrices', 'salesPSF', 'salesPriceDistribution', 'salesDistribution', 'luxVsTraditional', 'priceIndex'].includes(selectedChart.name)
      ? `${effectiveStartYear ? `&yearStart=${effectiveStartYear}` : ''}${effectiveEndYear ? `&yearEnd=${effectiveEndYear}` : ''}`
      : '';
    const catLuxParam = buildingCategoryId === -1 ? '&luxOption=true'
                      : buildingCategoryId === -2 ? '&luxFullService=true'
                      : '';
    const catIdParam = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';

    const scopeOnly = isNeighborhoodMode
      ? `neighborhoodId=${selectedNeighborhood!.id}`
      : `marketId=${selectedMarket!.id}`;

    const endpoints: Record<string, string> = {
      monthlyClosings:        `/api/charts/monthly-closings?${base}${catLuxParam}${catIdParam}`,
      millionDollarMarket:    `/api/charts/million-dollar-market?${base}${yearParams}${catLuxParam}${catIdParam}`,
      salesPrices:            `/api/charts/sales-prices?${base}${yearParams}${catLuxParam}${catIdParam}`,
      salesPSF:               `/api/charts/sales-psf?${base}${yearParams}${catLuxParam}${catIdParam}`,
      salesPriceDistribution: `/api/charts/price-range-trends?${base}${yearParams}${catLuxParam}${catIdParam}`,
      salesDistribution:      `/api/charts/sales-distribution?${base}${yearParams}`,
      luxVsTraditional:       `/api/charts/lux-vs-traditional?${scopeOnly}${yearParams}`,
      priceIndex:             `/api/charts/price-index?${base}${yearParams}`,
      shadowInventory:        `/api/charts/shadow-inventory?${scopeOnly}${subareaParam}${catLuxParam}${catIdParam}`,
    };
    const url = endpoints[selectedChart.name];

    if (!url) { setLoading(false); return; }

    authFetch(url, { signal: controller.signal })
      .then(r => r.json())
      .then(data => { if (!cancelled) { setChartData(data); setLoading(false); } })
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') setLoading(false);
      });

    return () => { cancelled = true; controller.abort(); };
  }, [selectedNeighborhood, selectedMarket, selectedChart, propertyType, buildingCategoryId, startYear, endYear, selectedSubarea]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showMortgagePercent || selectedChart?.name !== 'priceIndex') {
      setMortgagePctData(null);
      return;
    }
    const isNeighborhoodMode = !!selectedNeighborhood;
    const isMarketMode = !selectedNeighborhood && !!selectedMarket;
    if (!isNeighborhoodMode && !isMarketMode) return;

    const scopeParam = isNeighborhoodMode
      ? `neighborhoodId=${selectedNeighborhood!.id}`
      : `marketId=${selectedMarket!.id}`;
    const controller = new AbortController();
    authFetch(`/api/charts/mortgage-percent?${scopeParam}&propertyType=${propertyType}`, { signal: controller.signal })
      .then(r => r.json())
      .then(d => setMortgagePctData(d))
      .catch((err: unknown) => { if ((err as { name?: string })?.name !== 'AbortError') setMortgagePctData(null); });
    return () => controller.abort();
  }, [showMortgagePercent, selectedChart, selectedMarket, selectedNeighborhood, propertyType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showMortgageRate || selectedChart?.name !== 'priceIndex') {
      setMortgageRateData(null);
      return;
    }
    const controller = new AbortController();
    authFetch('/api/charts/mortgage-rate', { signal: controller.signal })
      .then(r => r.json())
      .then(d => setMortgageRateData(d))
      .catch((err: unknown) => { if ((err as { name?: string })?.name !== 'AbortError') setMortgageRateData(null); });
    return () => controller.abort();
  }, [showMortgageRate, selectedChart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedMarket) { setTimePeriods([]); return; }
    const nhParam = selectedNeighborhood ? `&neighborhoodId=${selectedNeighborhood.id}` : '';
    authFetch(`/api/charts/time-periods?marketId=${selectedMarket.id}${nhParam}`)
      .then(r => r.json())
      .then(setTimePeriods)
      .catch(() => setTimePeriods([]));
  }, [selectedMarket, selectedNeighborhood]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadBuildingAppreciationPDF = async () => {
    if (!buildingAppPdfTableRef.current || !buildingAppreciationData?.length) return;
    const locationName = selectedNeighborhood?.name ?? selectedMarket?.name ?? '';
    const catLabel = buildingCategoryId ? (BUILDING_CATEGORIES.find(c => c.value === buildingCategoryId)?.clean || null) : null;
    const sortSuffix = buildingAppSortCol === 'appreciation' ? '' : ` by ${BUILDING_APP_SORT_LABELS[buildingAppSortCol]}`;
    const subtitle = buildingAppPage === 0
      ? `${locationName} | Top Ten ${catLabel ? `${catLabel} ` : ''}Buildings${sortSuffix}`
      : `${locationName} | ${catLabel ? `${catLabel} ` : ''}Buildings ${appRangeStart}-${appRangeEnd}${sortSuffix}`;
    const title = `${selectedChart?.title ?? 'Building Appreciation'} — ${locationName}`;
    const pageW = 11, pageH = 8.5, marginX = 0.5, marginTop = 1.0, marginBot = 1.0;

    const canvas = await html2canvas(buildingAppPdfTableRef.current, { scale: 2, backgroundColor: '#ffffff' });
    const availW = pageW - 2 * marginX;
    const availH = pageH - marginTop - marginBot;
    const widthIn = canvas.width / 2 / 96;
    const heightIn = canvas.height / 2 / 96;
    const fit = Math.min(availW / widthIn, availH / heightIn, 1);
    const imgW = widthIn * fit;
    const imgH = heightIn * fit;

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });

    // Title + subtitle centered in top margin
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text(selectedChart?.title ?? 'Building Appreciation', pageW / 2, marginTop / 2 - 0.1, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor('#66635F');
    pdf.text(subtitle, pageW / 2, marginTop / 2 + 0.15, { align: 'center' });
    pdf.setTextColor('#000000');

    // Table centered in available area
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', (pageW - imgW) / 2, marginTop + (availH - imgH) / 2, imgW, imgH);

    // Org logo: top-left, 0.5" from edge, 0.5" tall
    const orgLogoUrl = orgInfo?.logoUrl
      ? `${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${orgInfo.id}/logo-bytes`
      : null;
    if (orgLogoUrl) {
      const logoImg = await new Promise<HTMLImageElement | null>(resolve => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = () => resolve(null);
        i.src = orgLogoUrl;
      });
      if (logoImg) {
        const logoH = 0.5;
        const logoW = (logoImg.naturalWidth / logoImg.naturalHeight) * logoH;
        const c = document.createElement('canvas');
        c.width = logoImg.naturalWidth; c.height = logoImg.naturalHeight;
        c.getContext('2d')!.drawImage(logoImg, 0, 0);
        pdf.addImage(c.toDataURL('image/png'), 'PNG', 0.5, 0.5, logoW, logoH);
      }
    }

    // Prism logo: bottom-right, 0.5" from edge, 0.5" tall
    const prismPixH = 200;
    const prismPixW = Math.round(PRISM_LOGO_ASPECT * prismPixH);
    try {
      const res = await fetch(prismLogo as string);
      let text = await res.text();
      text = text.replace(/<svg\b/, `<svg width="${prismPixW}" height="${prismPixH}"`);
      const blob = new Blob([text], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const prismImg = await new Promise<HTMLImageElement | null>(resolve => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => resolve(null);
        i.src = url;
      });
      URL.revokeObjectURL(url);
      if (prismImg) {
        const prismH = 0.5;
        const prismW = PRISM_LOGO_ASPECT * prismH;
        const c2 = document.createElement('canvas');
        c2.width = prismPixW; c2.height = prismPixH;
        c2.getContext('2d')!.drawImage(prismImg, 0, 0);
        pdf.addImage(c2.toDataURL('image/png'), 'PNG', pageW - prismW - 0.5, pageH - prismH - 0.5, prismW, prismH);
      }
    } catch {}

    pdf.save(`${title}.pdf`);
  };

  const downloadClosingNowPDF = async () => {
    if (!closingNowPdfTableRef.current || !closingNowData?.length) return;
    const locationName = selectedNeighborhood?.name ?? selectedMarket?.name ?? '';
    const subtitle = `${locationName}${closingNowCatLabel ? ` | ${closingNowCatLabel}` : ''}`;
    const title = `${selectedChart?.title ?? 'Closing Now'} — ${locationName}`;
    const pageW = 11, pageH = 8.5, marginX = 0.5, marginTop = 1.0, marginBot = 1.0;

    const canvas = await html2canvas(closingNowPdfTableRef.current, { scale: 2, backgroundColor: '#ffffff' });
    const availW = pageW - 2 * marginX;
    const availH = pageH - marginTop - marginBot;
    const widthIn = canvas.width / 2 / 96;
    const heightIn = canvas.height / 2 / 96;
    const fit = Math.min(availW / widthIn, availH / heightIn, 1);
    const imgW = widthIn * fit;
    const imgH = heightIn * fit;

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text(selectedChart?.title ?? 'Closing Now', pageW / 2, marginTop / 2 - 0.1, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor('#66635F');
    pdf.text(subtitle, pageW / 2, marginTop / 2 + 0.15, { align: 'center' });
    pdf.setTextColor('#000000');

    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', (pageW - imgW) / 2, marginTop + (availH - imgH) / 2, imgW, imgH);

    const orgLogoUrl = orgInfo?.logoUrl
      ? `${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${orgInfo.id}/logo-bytes`
      : null;
    if (orgLogoUrl) {
      const logoImg = await new Promise<HTMLImageElement | null>(resolve => {
        const i = new Image(); i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i); i.onerror = () => resolve(null); i.src = orgLogoUrl;
      });
      if (logoImg) {
        const logoH = 0.5, logoW = (logoImg.naturalWidth / logoImg.naturalHeight) * logoH;
        const c = document.createElement('canvas');
        c.width = logoImg.naturalWidth; c.height = logoImg.naturalHeight;
        c.getContext('2d')!.drawImage(logoImg, 0, 0);
        pdf.addImage(c.toDataURL('image/png'), 'PNG', 0.5, 0.5, logoW, logoH);
      }
    }

    const prismPixH = 200, prismPixW = Math.round(PRISM_LOGO_ASPECT * prismPixH);
    try {
      const res = await fetch(prismLogo as string);
      let text = await res.text();
      text = text.replace(/<svg\b/, `<svg width="${prismPixW}" height="${prismPixH}"`);
      const blob = new Blob([text], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const prismImg = await new Promise<HTMLImageElement | null>(resolve => {
        const i = new Image(); i.onload = () => resolve(i); i.onerror = () => resolve(null); i.src = url;
      });
      URL.revokeObjectURL(url);
      if (prismImg) {
        const prismH = 0.5, prismW = PRISM_LOGO_ASPECT * prismH;
        const c2 = document.createElement('canvas');
        c2.width = prismPixW; c2.height = prismPixH;
        c2.getContext('2d')!.drawImage(prismImg, 0, 0);
        pdf.addImage(c2.toDataURL('image/png'), 'PNG', pageW - prismW - 0.5, pageH - prismH - 0.5, prismW, prismH);
      }
    } catch {}

    pdf.save(`${title}.pdf`);
  };

  const handleMarketNavigate = (market: Market) => {
    if (!market.hasAccess) return;
    navigate(`/market/${market.id}`);
  };

  const handleMarketToggle = (e: React.MouseEvent, market: Market) => {
    e.stopPropagation();
    const willExpand = !expandedMarkets.includes(market.id);
    setExpandedMarkets(prev =>
      willExpand ? [...prev, market.id] : prev.filter(id => id !== market.id)
    );
    if (willExpand) loadNeighborhoods(market);
  };

  const handleNeighborhoodClick = (neighborhood: Neighborhood) => {
    if (!neighborhood.hasAccess) return;
    navigate(`/neighborhood/${neighborhood.id}`);
  };

  const showYearRange = ['millionDollarMarket', 'salesPrices', 'salesPSF', 'salesPriceDistribution', 'salesDistribution', 'luxVsTraditional', 'priceIndex'].includes(selectedChart?.name ?? '');

  const APP_PAGE_SIZE = 10;
  const handleBuildingAppSort = (col: typeof buildingAppSortCol) => {
    if (col === buildingAppSortCol) {
      setBuildingAppSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setBuildingAppSortCol(col);
      setBuildingAppSortDir('asc');
    }
    setBuildingAppPage(0);
  };
  const buildingAppDir = buildingAppSortDir === 'asc' ? 1 : -1;
  const sortedBuildingAppData = buildingAppreciationData
    ? [...buildingAppreciationData].sort((a, b) => {
        switch (buildingAppSortCol) {
          case 'building':      return buildingAppDir * sortableBuildingName(a.building).localeCompare(sortableBuildingName(b.building));
          case 'address': {
            if (!a.buildingAddress && !b.buildingAddress) return 0;
            if (!a.buildingAddress) return 1;
            if (!b.buildingAddress) return -1;
            return buildingAppDir * a.buildingAddress.localeCompare(b.buildingAddress);
          }
          case 'units': {
            if (a.units == null && b.units == null) return 0;
            if (a.units == null) return 1;
            if (b.units == null) return -1;
            return buildingAppDir * (a.units - b.units);
          }
          case 'resales':      return buildingAppDir * (a.resales - b.resales);
          case 'openDate':     return buildingAppDir * a.earliestSale.localeCompare(b.earliestSale);
          case 'mostRecent':   return buildingAppDir * a.mostRecentSale.localeCompare(b.mostRecentSale);
          case 'appreciation': return buildingAppDir * (a.annualizedAppreciation - b.annualizedAppreciation);
          default: return 0;
        }
      })
    : null;
  const appTotalPages = sortedBuildingAppData ? Math.ceil(sortedBuildingAppData.length / APP_PAGE_SIZE) : 0;
  const appPagedData  = sortedBuildingAppData ? sortedBuildingAppData.slice(buildingAppPage * APP_PAGE_SIZE, (buildingAppPage + 1) * APP_PAGE_SIZE) : [];
  const appSortSuffix = buildingAppSortCol === 'appreciation' ? '' : ` by ${BUILDING_APP_SORT_LABELS[buildingAppSortCol]}`;
  const appRangeStart = buildingAppPage * APP_PAGE_SIZE + 1;
  const appRangeEnd   = sortedBuildingAppData ? Math.min((buildingAppPage + 1) * APP_PAGE_SIZE, sortedBuildingAppData.length) : appRangeStart;
  const appDisplayPage = buildingAppPage + 1;
  const appPageNums: (number | '...')[] = appTotalPages <= 7
    ? Array.from({ length: appTotalPages }, (_, i) => i + 1)
    : appDisplayPage <= 4
      ? [1, 2, 3, 4, 5, '...', appTotalPages]
      : appDisplayPage >= appTotalPages - 3
        ? [1, '...', appTotalPages - 4, appTotalPages - 3, appTotalPages - 2, appTotalPages - 1, appTotalPages]
        : [1, '...', appDisplayPage - 1, appDisplayPage, appDisplayPage + 1, '...', appTotalPages];
  const appPgBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.25rem 0.6rem', fontFamily: "'Montserrat', sans-serif", fontSize: 13,
    border: '1px solid #ddd', borderRadius: 3, cursor: 'pointer',
    background: active ? '#958945' : '#fff', color: active ? '#fff' : '#333',
    fontWeight: active ? 700 : 400,
  });
  const appSortTh = (col: typeof buildingAppSortCol, label: string, align: 'left' | 'center' | 'right' = 'left') => (
    <th
      onClick={() => handleBuildingAppSort(col)}
      style={{
        ...thStyle,
        textAlign: align,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {label}{buildingAppSortCol === col ? (buildingAppSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const CLOSING_NOW_PAGE_SIZE = 10;
  const closingNowTotalPages  = closingNowData ? Math.ceil(closingNowData.length / CLOSING_NOW_PAGE_SIZE) : 0;
  const closingNowPagedData   = closingNowData ? closingNowData.slice(closingNowPage * CLOSING_NOW_PAGE_SIZE, (closingNowPage + 1) * CLOSING_NOW_PAGE_SIZE) : [];
  const closingNowPdfData     = closingNowData ? closingNowData.slice(0, CLOSING_NOW_PAGE_SIZE) : [];
  const closingNowCatLabel    = BUILDING_CATEGORIES.find(c => c.value === buildingCategoryId)?.clean || '';

  const thStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', fontWeight: 600, fontFamily: "'Montserrat', sans-serif" };
  const tdStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', verticalAlign: 'top', fontFamily: "'Montserrat', sans-serif" };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 61px)' }}>

      {/* Left accordion panel */}
      <div style={{
        width: 220, flexShrink: 0, borderRight: '1px solid #ddd',
        padding: 0, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 0,
        background: '#f6f5f1', borderTop: '1px solid #ddd'
      }}>
        <div style={{ padding: '1rem', fontFamily: "'Montserrat', sans-serif", fontSize: 9, fontWeight: 700, color: '#958945', letterSpacing: 3 }}>ACTIVE MARKETS</div>
        {markets.filter(m => m.hasAccess).map(market => {
          const isExpanded = expandedMarkets.includes(market.id);
          const isActiveMarket = selectedMarket?.id === market.id && selectedNeighborhood === null;
          const neighborhoods = neighborhoodsByMarket[market.id] ?? [];

          return (
            <React.Fragment key={market.id}>
              <div
                onClick={() => handleMarketNavigate(market)}
                style={{
                  padding: '0.75rem',
                  cursor: market.hasAccess ? 'pointer' : 'default',
                  backgroundColor: !market.hasAccess ? LOCKED_BG : isActiveMarket ? ACTIVE_BG : '#f6f5f1',
                  color: !market.hasAccess ? LOCKED_TEXT : isActiveMarket ? '#ffffff' : '#403B36',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: isActiveMarket ? 'bold' : 400,
                  fontSize: 15,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none',
                  borderBottom: '1px solid #ddd',
                  boxShadow: isActiveMarket ? 'inset 3px 0 0 #bfb261' : 'none',
                }}
              >
                <span>{market.name}</span>
                {market.hasAccess && (
                  <span
                    onClick={e => handleMarketToggle(e, market)}
                    style={{ fontSize: 10, marginLeft: 6, padding: '0 0.25rem', cursor: 'pointer' }}
                  >
                    {isExpanded ? '▲' : '▼'}
                  </span>
                )}
              </div>

              {isExpanded && neighborhoods.map(n => {
                const isActive = selectedNeighborhood?.id === n.id;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNeighborhoodClick(n)}
                    title={n.hasAccess ? `Click to view ${n.name} insights` : `Click to add ${n.name} to your subscription`}
                    style={{
                      padding: '0.75rem 0.75rem 0.75rem 1.5rem',
                      cursor: n.hasAccess ? 'pointer' : 'default',
                      background: isActive ? ACTIVE_BG : !n.hasAccess ? LOCKED_BG : '#f6f5f1',
                      color: isActive ? ACTIVE_TEXT : !n.hasAccess ? LOCKED_TEXT : '#403B36',
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: isActive ? 'bold' : 400,
                      fontSize: 13,
                      borderBottom: '1px solid #ddd',
                      boxShadow: isActive ? 'inset 3px 0 0 #bfb261' : 'none',
                    }}
                  >
                    {n.name}
                  </div>
                );
              })}

              {isExpanded && neighborhoods.length === 0 && (
                <div style={{ padding: '0.45rem 0.75rem', color: '#aaa', fontSize: 13, fontFamily: "'Montserrat', sans-serif", borderBottom: '1px solid #ddd' }}>
                  Loading...
                </div>
              )}
            </React.Fragment>
          );
        })}
        <div style={{ marginTop: 'auto', padding: '1rem' }}>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 8, fontWeight: 400, color: '#8C877D', letterSpacing: 2.5 }}>POWERED BY</div>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 10, fontWeight: 700, color: '#736E66', letterSpacing: 2 }}>LINK ANALYTICS</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0, background: '#ffffff', display: 'flex', flexDirection: 'column' }}>

        {/* Hero banner */}
        {(selectedNeighborhood || selectedMarket) && (
          <div style={{
            height: 184,
            backgroundImage: (selectedNeighborhood?.photoUrl ?? selectedMarket?.photoUrl)
              ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${selectedNeighborhood?.photoUrl ?? selectedMarket?.photoUrl})`
              : undefined,
            backgroundColor: '#ccc',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'flex-end',
            padding: '0 2rem 0.25rem',
            flexShrink: 0,
            overflow: 'hidden',
          }}>
            <span
              onClick={() => {
                const ptParam  = propertyType !== 'C' ? `?propertyType=${propertyType}` : '';
                const catParam = buildingCategoryId ? `${ptParam ? '&' : '?'}buildingCategoryId=${buildingCategoryId}` : '';
                const base     = selectedNeighborhood ? `/neighborhood/${selectedNeighborhood.id}` : `/market/${selectedMarket!.id}`;
                navigate(`${base}${ptParam}${catParam}`);
              }}
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '64pt',
                fontWeight: 'bold',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 0.6,
                letterSpacing: '-1px',
                cursor: 'pointer',
              }}
            >
              {selectedNeighborhood?.name ?? selectedMarket?.name}
            </span>
          </div>
        )}

        {/* Controls */}
        <div style={{ padding: '2rem', flex: 1 }}>

        {/* Property Type / Building Category filters */}
        {(selectedNeighborhood || selectedMarket) && (
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
            {selectedMarket && (
              <select
                value={propertyType}
                onChange={e => {
                  const newType = e.target.value;
                  setPropertyType(newType);
                  if (newType !== 'C') {
                    if (['salesDistribution', 'salesPSF', 'luxVsTraditional', 'closingNow', 'shadowInventory'].includes(selectedChart?.name ?? '')) {
                      const fallback = chartOptions.find(c => c.name === 'salesPrices')
                        ?? chartOptions.find(c => !['salesDistribution', 'salesPSF', 'luxVsTraditional', 'closingNow'].includes(c.name))
                        ?? null;
                      setSelectedChart(fallback);
                    }
                    setBuildingCategoryId(null);
                  }
                }}
                style={{ padding: '0.5rem', fontSize: 16 }}
              >
                {PROPERTY_TYPES.map(pt => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            )}
            {propertyType === 'C' && ['buildingAppreciation', 'closingNow', 'monthlyClosings', 'millionDollarMarket', 'salesPrices', 'salesPSF', 'salesPriceDistribution', 'shadowInventory'].includes(selectedChart?.name ?? '') && (
              <select
                value={buildingCategoryId ?? 0}
                onChange={e => {
                  const v = Number(e.target.value);
                  setBuildingCategoryId(v === 0 ? null : v);
                }}
                style={{ padding: '0.5rem', fontSize: 16 }}
              >
                {BUILDING_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {chartOptions.length > 1 && (
            <select
              value={selectedChart?.id ?? ''}
              onChange={e => {
                if (e.target.value === 'home') {
                  const ptParam  = propertyType !== 'C' ? `?propertyType=${propertyType}` : '';
                  const catParam = buildingCategoryId ? `${ptParam ? '&' : '?'}buildingCategoryId=${buildingCategoryId}` : '';
                  const subParam = selectedNeighborhood && selectedSubarea ? `${ptParam || catParam ? '&' : '?'}subareaId=${selectedSubarea.id}` : '';
                  navigate(selectedNeighborhood ? `/neighborhood/${selectedNeighborhood.id}${ptParam}${catParam}${subParam}` : `/market/${selectedMarket!.id}${ptParam}${catParam}`);
                  return;
                }
                setSelectedChart(chartOptions.find(c => c.id === Number(e.target.value)) ?? null);
              }}
              style={{ padding: '0.5rem', fontSize: 16 }}
            >
              <option value="home">{(selectedNeighborhood?.name ?? selectedMarket?.name)} Home</option>
              {chartOptions
                .filter(c => {
                  const condoOnly = c.name === 'salesDistribution' || c.name === 'salesPSF' || c.name === 'luxVsTraditional' || c.name === 'closingNow' || c.name === 'shadowInventory';
                  if (condoOnly && propertyType !== 'C') return false;
                  const needsBuildings = c.name === 'closingNow';
                  if (needsBuildings && selectedNeighborhood && buildingOpenings.length === 0) return false;
                  return true;
                })
                .map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          )}

          {selectedChart?.description && (
            <button
              onClick={() => setShowChartDescription(true)}
              aria-label={`About ${selectedChart.title}`}
              title={`About ${selectedChart.title}`}
              style={{
                width: 22, height: 22, padding: 0, cursor: 'pointer',
                border: '1px solid #ccc', borderRadius: '50%', background: '#fff',
                color: '#888', fontSize: 12, fontStyle: 'italic', fontWeight: 700,
                lineHeight: '20px', textAlign: 'center',
              }}
            >
              i
            </button>
          )}

          {subareas.length > 0 && selectedChart?.name !== 'priceIndex' && (
            <select
              value={selectedSubarea?.id ?? ''}
              onChange={e => {
                const id = Number(e.target.value);
                setSelectedSubarea(subareas.find(s => s.id === id) ?? null);
              }}
              style={{ padding: '0.5rem', fontSize: 16 }}
            >
              <option value="">All Subareas</option>
              {subareas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}

          {selectedChart?.name === 'priceIndex' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={showMortgagePercent}
                onChange={e => setShowMortgagePercent(e.target.checked)}
              />
              Show Mortgage %
            </label>
          )}

          {selectedChart?.name === 'priceIndex' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={showMortgageRate}
                onChange={e => setShowMortgageRate(e.target.checked)}
              />
              Show 30yr Rate
            </label>
          )}

          {selectedChart?.name === 'buildingAppreciation' && (
            <select
              value={buildingAppSortCol === 'openDate' ? 'openDate' : 'appreciation'}
              onChange={e => {
                const v = e.target.value as 'appreciation' | 'openDate';
                setBuildingAppSortCol(v);
                setBuildingAppSortDir('desc');
                setBuildingAppPage(0);
              }}
              style={{ padding: '0.5rem', fontSize: 16 }}
            >
              <option value="appreciation">Sort by Appreciation</option>
              <option value="openDate">Sort by Building Open Date</option>
            </select>
          )}

          {showYearRange && startYear !== null && endYear !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 280 }}>
              <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>{sliderValue[0]}</span>
              <Slider
                range
                min={2000}
                max={CURRENT_YEAR}
                value={sliderValue}
                onChange={val => {
                  const [s, e] = val as number[];
                  setSliderValue([s, e]);
                }}
                onChangeComplete={val => {
                  const [s, e] = val as number[];
                  setStartYear(s);
                  setEndYear(e);
                }}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>{sliderValue[1]}</span>
            </div>
          )}

          {propertyType === 'C' && ['salesPrices', 'salesPSF'].includes(selectedChart?.name ?? '') && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={showBuildingOpenings}
                onChange={e => setShowBuildingOpenings(e.target.checked)}
              />
              Show building opening labels
            </label>
          )}
        </div>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem 0' }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: '4px solid #e0dbd7',
              borderTopColor: '#ba9394',
              animation: 'prism-spin 0.75s linear infinite',
            }} />
          </div>
        )}

        {chartData && selectedChart && (selectedNeighborhood || selectedMarket) && (
          <ChartRenderer
            chartName={selectedChart.name}
            chartTitle={selectedChart.title}
            neighborhoodId={selectedNeighborhood?.id ?? 0}
            neighborhoodName={selectedNeighborhood?.name ?? selectedMarket?.name ?? ''}
            propertyTypeLabel={PROPERTY_TYPES.find(pt => pt.value === propertyType)?.label ?? propertyType}
            buildingCategoryLabel={BUILDING_CATEGORIES.find(c => c.value === buildingCategoryId)?.clean || undefined}
            data={(() => {
              if (selectedChart.name !== 'priceIndex') return chartData;
              let series = [...chartData.series];
              if (showMortgagePercent && mortgagePctData) {
                const pctMap = new Map(mortgagePctData.categories.map((cat, i) => [cat, mortgagePctData.data[i]]));
                series = [...series, { name: 'Mortgage %', data: chartData.categories.map(cat => pctMap.get(cat) ?? null) }];
              }
              if (showMortgageRate && mortgageRateData) {
                const rateMap = new Map(mortgageRateData.categories.map((cat, i) => [cat, mortgageRateData.data[i]]));
                series = [...series, { name: '30yr Rate', data: chartData.categories.map(cat => rateMap.get(cat) ?? null) }];
              }
              return { ...chartData, series };
            })()}
            buildingOpenings={propertyType === 'C' && showBuildingOpenings ? buildingOpenings : []}
            timePeriods={timePeriods}
            orgLogoUrl={orgInfo?.logoUrl ? `${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${orgInfo.id}/logo-bytes` : undefined}
            onAddToPackage={(hiddenSeries) => addItem({
              entityType: selectedNeighborhood ? 'neighborhood' : 'market',
              entityId: selectedNeighborhood?.id ?? selectedMarket!.id,
              entityName: selectedNeighborhood?.name ?? selectedMarket?.name ?? '',
              chartName: selectedChart.name,
              chartTitle: selectedChart.title,
              propertyType: propertyType,
              buildingCategoryId: buildingCategoryId,
              yearStart: startYear,
              yearEnd: endYear,
              subareaId: selectedSubarea?.id ?? null,
              subareaName: selectedSubarea?.name ?? null,
              sortOrder: null,
              hiddenSeries: hiddenSeries.length ? hiddenSeries : undefined,
            })}
          />
        )}

        {showChartDescription && selectedChart?.description && (
          <ChartDescriptionModal
            title={selectedChart.title}
            description={selectedChart.description}
            onClose={() => setShowChartDescription(false)}
          />
        )}

        {sortedBuildingAppData && selectedChart?.name === 'buildingAppreciation' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: 500, color: '#000000', lineHeight: 1.1 }}>
                  {selectedChart?.title ?? 'Building Appreciation'}
                </div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: 500, color: '#66635F' }}>
                  {(selectedNeighborhood?.name ?? selectedMarket?.name)} | {buildingAppPage === 0 ? 'Top Ten ' : ''}{buildingCategoryId ? `${BUILDING_CATEGORIES.find(c => c.value === buildingCategoryId)?.clean} ` : ''}Buildings{buildingAppPage > 0 ? ` ${appRangeStart}-${appRangeEnd}` : ''}{appSortSuffix}
                </div>
              </div>
              {showPdfLens && sortedBuildingAppData.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginTop: '0.5rem' }}>
                  <button
                    onClick={() => addItem({
                      entityType: selectedNeighborhood ? 'neighborhood' : 'market',
                      entityId: selectedNeighborhood?.id ?? selectedMarket!.id,
                      entityName: selectedNeighborhood?.name ?? selectedMarket?.name ?? '',
                      chartName: 'buildingAppreciation',
                      chartTitle: selectedChart?.title ?? 'Building Appreciation',
                      propertyType: null,
                      buildingCategoryId: buildingCategoryId,
                      yearStart: null,
                      yearEnd: null,
                      subareaId: selectedSubarea?.id ?? null,
                      subareaName: selectedSubarea?.name ?? null,
                      sortOrder: `${buildingAppSortCol === 'openDate' ? 'openDate' : 'appreciation'}${buildingAppPage > 0 ? `:${appRangeStart}-${appRangeEnd}` : ''}`,
                    })}
                    style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}
                  >
                    + Add to Lens
                  </button>
                  <button
                    onClick={downloadBuildingAppreciationPDF}
                    style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}
                  >
                    Download PDF
                  </button>
                </div>
              )}
            </div>

            {sortedBuildingAppData.length === 0 ? (
              <p style={{ color: '#666' }}>No resale data available for this selection.</p>
            ) : (
              <>
                {/* Visible paginated table */}
                <table ref={buildingAppTableRef} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                      {appSortTh('building', 'Building')}
                      {appSortTh('address', 'Address')}
                      {appSortTh('units', 'Units', 'center')}
                      {appSortTh('resales', 'Resales', 'center')}
                      {appSortTh('openDate', 'Open Date')}
                      {appSortTh('mostRecent', 'Most Recent Sale')}
                      {appSortTh('appreciation', 'Ann. Appreciation', 'right')}
                    </tr>
                  </thead>
                  <tbody>
                    {appPagedData.map((row, i) => (
                      <tr key={row.buildingId} onClick={() => navigate(`/building/${row.buildingId}`)} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA', cursor: 'pointer' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{row.building}</div>
                          <div style={{ fontSize: 12, color: '#888' }}>{row.buildingCategory}</div>
                        </td>
                        <td style={tdStyle}>
                          <div>{row.buildingAddress ?? '—'}</div>
                          {!selectedNeighborhood && (
                            <div style={{ fontSize: 12, color: '#888' }}>{row.neighborhood}</div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.units ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.resales}</td>
                        <td style={tdStyle}>{formatAppDate(row.earliestSale)}</td>
                        <td style={tdStyle}>{formatAppDate(row.mostRecentSale)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: row.annualizedAppreciation < 0 ? 'rgb(214,10,34)' : '#1CA11C' }}>
                          {(row.annualizedAppreciation * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {appTotalPages > 1 && (
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', justifyContent: 'center', marginTop: '1.25rem' }}>
                    <button
                      onClick={() => setBuildingAppPage(p => Math.max(0, p - 1))}
                      disabled={buildingAppPage === 0}
                      style={{ ...appPgBtnStyle(false), opacity: buildingAppPage === 0 ? 0.4 : 1 }}
                    >
                      ← Prev
                    </button>
                    {appPageNums.map((n, i) =>
                      n === '...'
                        ? <span key={`ellipsis-${i}`} style={{ padding: '0 0.25rem', fontSize: 13, color: '#999' }}>…</span>
                        : <button key={n} onClick={() => setBuildingAppPage(n - 1)} style={appPgBtnStyle(n === appDisplayPage)}>{n}</button>
                    )}
                    <button
                      onClick={() => setBuildingAppPage(p => Math.min(appTotalPages - 1, p + 1))}
                      disabled={buildingAppPage === appTotalPages - 1}
                      style={{ ...appPgBtnStyle(false), opacity: buildingAppPage === appTotalPages - 1 ? 0.4 : 1 }}
                    >
                      Next →
                    </button>
                  </div>
                )}

                {/* Hidden off-screen table used for PDF — renders the currently viewed page */}
                <table ref={buildingAppPdfTableRef} style={{ position: 'absolute', left: -9999, top: -9999, width: 1100, borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                      <th style={thStyle}>Building</th>
                      <th style={thStyle}>Address</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Units</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Resales</th>
                      <th style={thStyle}>Open Date</th>
                      <th style={thStyle}>Most Recent Sale</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Ann. Appreciation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appPagedData.map((row, i) => (
                      <tr key={row.buildingId} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{row.building}</div>
                          <div style={{ fontSize: 12, color: '#888' }}>{row.buildingCategory}</div>
                        </td>
                        <td style={tdStyle}>
                          <div>{row.buildingAddress ?? '—'}</div>
                          {!selectedNeighborhood && (
                            <div style={{ fontSize: 12, color: '#888' }}>{row.neighborhood}</div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.units ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.resales}</td>
                        <td style={tdStyle}>{formatAppDate(row.earliestSale)}</td>
                        <td style={tdStyle}>{formatAppDate(row.mostRecentSale)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: row.annualizedAppreciation < 0 ? 'rgb(214,10,34)' : '#1CA11C' }}>
                          {(row.annualizedAppreciation * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {closingNowData && selectedChart?.name === 'closingNow' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: 500, color: '#000000', lineHeight: 1.1 }}>
                  {selectedChart?.title ?? 'Closing Now'}
                </div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: 500, color: '#66635F' }}>
                  {selectedNeighborhood?.name ?? selectedMarket?.name}{closingNowCatLabel ? ` | ${closingNowCatLabel}` : ''}
                </div>
              </div>
              {showPdfLens && closingNowData.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginTop: '0.5rem' }}>
                  <button
                    onClick={() => addItem({
                      entityType: selectedNeighborhood ? 'neighborhood' : 'market',
                      entityId: selectedNeighborhood?.id ?? selectedMarket!.id,
                      entityName: selectedNeighborhood?.name ?? selectedMarket?.name ?? '',
                      chartName: 'closingNow',
                      chartTitle: selectedChart?.title ?? 'Closing Now',
                      propertyType: null,
                      buildingCategoryId: buildingCategoryId,
                      yearStart: null,
                      yearEnd: null,
                      subareaId: selectedSubarea?.id ?? null,
                      subareaName: selectedSubarea?.name ?? null,
                      sortOrder: null,
                    })}
                    style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}
                  >
                    + Add to Lens
                  </button>
                  <button onClick={downloadClosingNowPDF} style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}>
                    Download PDF
                  </button>
                </div>
              )}
            </div>

            {closingNowData.length === 0 ? (
              <p style={{ color: '#666' }}>No data available for this selection.</p>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                      <th style={thStyle}>Building</th>
                      <th style={thStyle}>Address</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Total Units</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Units Sold</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>% Sold Out</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Average Price</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Average PSF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closingNowPagedData.map((row, i) => (
                      <tr key={row.buildingId} onClick={() => navigate(`/building/${row.buildingId}`)} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA', cursor: 'pointer' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{row.building}</div>
                          <div style={{ fontSize: 12, color: '#888' }}>{row.buildingCategory}</div>
                        </td>
                        <td style={tdStyle}>
                          <div>{row.address ?? '—'}</div>
                          {!selectedNeighborhood && <div style={{ fontSize: 12, color: '#888' }}>{row.neighborhood}</div>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.totalUnits ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.unitsSold ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.pctSoldOut != null ? `${(row.pctSoldOut * 100).toFixed(1)}%` : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avgPrice != null ? `$${row.avgPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avgPsf != null ? `$${row.avgPsf.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {closingNowTotalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1rem', fontSize: 14 }}>
                    <button onClick={() => setClosingNowPage(p => p - 1)} disabled={closingNowPage === 0}
                      style={{ padding: '0.3rem 0.75rem', cursor: closingNowPage === 0 ? 'default' : 'pointer', border: '1px solid #ddd', borderRadius: 4, background: '#fff', opacity: closingNowPage === 0 ? 0.4 : 1 }}>
                      &lsaquo; Previous
                    </button>
                    <span style={{ color: '#666' }}>Page {closingNowPage + 1} of {closingNowTotalPages}</span>
                    <button onClick={() => setClosingNowPage(p => p + 1)} disabled={closingNowPage === closingNowTotalPages - 1}
                      style={{ padding: '0.3rem 0.75rem', cursor: closingNowPage === closingNowTotalPages - 1 ? 'default' : 'pointer', border: '1px solid #ddd', borderRadius: 4, background: '#fff', opacity: closingNowPage === closingNowTotalPages - 1 ? 0.4 : 1 }}>
                      Next &rsaquo;
                    </button>
                  </div>
                )}

                {/* Hidden off-screen table for PDF — always first 10 */}
                <table ref={closingNowPdfTableRef} style={{ position: 'absolute', left: -9999, top: -9999, width: 1100, borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                      <th style={thStyle}>Building</th>
                      <th style={thStyle}>Address</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Total Units</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Units Sold</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>% Sold Out</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Average Price</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Average PSF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closingNowPdfData.map((row, i) => (
                      <tr key={row.buildingId} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{row.building}</div>
                          <div style={{ fontSize: 12, color: '#888' }}>{row.buildingCategory}</div>
                        </td>
                        <td style={tdStyle}>
                          <div>{row.address ?? '—'}</div>
                          {!selectedNeighborhood && <div style={{ fontSize: 12, color: '#888' }}>{row.neighborhood}</div>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.totalUnits ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.unitsSold ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.pctSoldOut != null ? `${(row.pctSoldOut * 100).toFixed(1)}%` : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avgPrice != null ? `$${row.avgPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avgPsf != null ? `$${row.avgPsf.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {!selectedNeighborhood && !selectedMarket && !loading && (
          <p style={{ color: '#666' }}>Select a market to view charts.</p>
        )}

        {(selectedNeighborhood || selectedMarket) && !selectedChart && chartOptions.length > 1 && !loading && (
          <p style={{ color: '#666' }}>Select a chart above.</p>
        )}
        </div>
      </div>
    </div>
  );
}
